import type { MenuProps } from 'antd';
import { Space } from 'antd';
import classNames from 'classnames';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import { SIGNATURE_STATUS } from 'constants/Global';
import { STATUS_FAILED, STATUS_LOADED, STATUS_LOADING } from 'constants/MediaConstants';
import { downloadFetchedURL } from 'modules/medias/actions';
import type { MediaListItem } from 'ui/base/MediaGallery/types';
import Tag from 'ui/base/SignatureMedia/Tag';

import { Button, Dropdown, MediaModal, Upload } from '..';
import { iconStyle } from './UI';

type ContentProps = {
  title?: string;
  hideTag?: boolean;
  disabled?: boolean;
  media?: MediaListItem;
  onDelete?: (media: MediaListItem) => void;
  onRetry?: (media: MediaListItem) => void;
  signatureBox: keyof typeof SIGNATURE_STATUS;
  signatureStatus: keyof typeof SIGNATURE_STATUS;
  onBeforeUpload?: () => void;
};

const Content: React.FC<ContentProps> = ({ title, media, signatureStatus, signatureBox, hideTag, onDelete, onBeforeUpload, onRetry, disabled }) => {
  const dispatch = useDispatch();
  const [openModal, setOpenModal] = React.useState(false);

  const { status, data } = media ?? {};
  const { path, filename } = data ?? {};

  const hasMedia = Boolean(media);
  const loading = status === STATUS_LOADING;
  const hasError = status === STATUS_FAILED;
  const hasPath = Boolean(path);

  const isSigned = signatureStatus === SIGNATURE_STATUS.SIGNED;
  const isPending = !isSigned && hasMedia;
  const displayTag = !hideTag && (isSigned || isPending);

  const showUploadButton = typeof onBeforeUpload === 'function' && !hasMedia && !disabled;
  const canRetry = hasError && typeof onRetry === 'function' && !disabled;
  const isUploaded = status === STATUS_LOADED;
  const isUploadedAndSaved = isUploaded && hasPath && Boolean(signatureStatus || signatureBox);

  const handleDelete = React.useCallback(() => {
    onDelete?.(media!);
  }, [onDelete, media]);

  const handleRetry = React.useCallback(() => {
    onRetry?.(media!);
  }, [onRetry, media]);

  const handleOpen = React.useCallback(() => {
    setOpenModal(true);
  }, []);

  const handleClose = React.useCallback(() => {
    setOpenModal(false);
  }, []);

  const handleDownload = React.useCallback(() => {
    dispatch(downloadFetchedURL(path!, filename!));
  }, [dispatch, path, filename]);

  const actionCollection: Record<string, () => void> = React.useMemo(() => ({
    download: handleDownload,
    delete: handleDelete,
  }), [handleDownload, handleDelete]);

  const handleOnClick: NonNullable<MenuProps['onClick']> = React.useCallback(({ key: actionKey }) => {
    actionCollection[actionKey]?.();
  }, [actionCollection]);

  const menu = React.useMemo(() => ({
    onClick: handleOnClick,
    items: [
      {
        key: 'download',
        hide: !isUploadedAndSaved,
        'data-cy': 'download-signature',
        label: <FormattedMessage id="download" defaultMessage="Download" />,
      },
      {
        hide: !isUploadedAndSaved || isSigned,
        type: 'divider' as const,
      },
      {
        disabled,
        danger: true,
        hide: isSigned,
        key: 'delete',
        'data-cy': 'delete-signature',
        label: <FormattedMessage id="delete" defaultMessage="Delete" />,
      },
    ],
  }), [isUploadedAndSaved, isSigned, handleOnClick, disabled]);

  return (
    <div className="d-flex align-items-center">

      <div className="d-flex align-items-center flex-grow-1">
        {title}
        {displayTag && <Tag status={signatureStatus} />}
      </div>

      {showUploadButton && (
        <Upload
          limit={1}
          data-cy="upload-signature"
          beforeUpload={onBeforeUpload}
          allowed={{ fileTypes: ['application/pdf'], extensions: ['.pdf'] }}
        >
          <Button
            type="text"
            icon={<span className={classNames('fb-icon--upload', iconStyle)} />}
          />
        </Upload>
      )}
      {hasMedia && (
        <Space size="small">
          {isUploadedAndSaved && (
            <Button
              loading={loading}
              onClick={handleOpen}
              icon={<i className={classNames('fb-icon--eye', iconStyle)} />}
              data-cy="watch-signature"
            />
          )}

          {canRetry && (
            <Button
              loading={loading}
              onClick={handleRetry}
              data-cy="retry-upload-signature"
              icon={<span className={classNames('fb-icon--refresh', iconStyle)} />}
            />
          )}

          {isUploaded && (
            <Dropdown menu={menu} placement="bottomRight">
              <Button data-cy="signature-menu-button" icon={<span className="fb-icon--elipsis-v" />} />
            </Dropdown>
          )}
        </Space>
      )}

      {/* @ts-expect-error MediaModal selectMedia prop not needed in this context */}
      {openModal && <MediaModal close={handleClose} selectedMedia={data} medias={[data]} />}
    </div>
  );
};

export default Content;
